import { useState } from "react";
import {
  ArrowRight,
  Check,
  Eye,
  FileSpreadsheet,
  FileText,
  GitBranch,
  Landmark,
  Lock,
  ScanLine,
  Sparkles,
  Wand2,
} from "lucide-react";
import { SIGN_UP_AUTH, SMART_IMPORT_DEMO_URL } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { useTranslation } from "@/common/providers/i18n-provider";
import { SMART_IMPORT_FAQ_KEYS } from "@/components/smart-import-meta";
import "@/styles/smart-import.css";

const STEPS = [
  {
    title: "smartImport.dropYourFile",
    body: "smartImport.csvPdfOfxQfxOrA",
    img: "/img/smart-import/screenshot-upload.png",
    alt: "smartImport.smartImportUploadScreenWithA",
    height: 621,
  },
  {
    title: "smartImport.reviewWhatAiExtracted",
    body: "smartImport.everyTransactionIsParsedIntoDate",
    img: "/img/smart-import/screenshot-review.png",
    alt: "smartImport.smartImportPreviewTableShowingSeven",
    height: 716,
  },
  {
    title: "smartImport.categorizeWithOneClick",
    body: "smartImport.pickTheSourceAccountOnceThen",
    img: "/img/smart-import/screenshot-categorize.png",
    alt: "smartImport.smartImportConfigureStepWithAi",
    height: 802,
  },
  {
    title: "smartImport.importAndYouReDone",
    body: "smartImport.reviewThenImportYourTransactionsLand",
    img: "/img/smart-import/screenshot-import.png",
    alt: "smartImport.smartImportSuccessScreenConfirmingThe",
    height: 426,
  },
] as const;

const FEATURES = [
  {
    icon: FileText,
    title: "smartImport.readsAnyStatementFormat",
    body: "smartImport.csvPdfOfxAndQfxExports",
  },
  {
    icon: Sparkles,
    title: "smartImport.aiExtractionNotTemplates",
    body: "smartImport.anAiModelReadsUnstructuredPdfs",
  },
  {
    icon: Wand2,
    title: "smartImport.categorizedToYourAccounts",
    body: "smartImport.oneClickOnAiFillSuggests",
  },
  {
    icon: Eye,
    title: "smartImport.youStayInControl",
    body: "smartImport.previewEditInlineOrRejectAny",
  },
  {
    icon: GitBranch,
    title: "smartImport.gitBackedAuditTrail",
    body: "smartImport.everyImportIsARealGit",
  },
  {
    icon: Lock,
    title: "smartImport.plainTextNoLockIn",
    body: "smartImport.transactionsLandAsCleanBeancountEntries",
  },
] as const;

const FORMATS = [
  {
    icon: FileSpreadsheet,
    name: "smartImport.csv",
    note: "smartImport.spreadsheetAndBankExportsParsedInstantly",
  },
  {
    icon: FileText,
    name: "smartImport.pdf",
    note: "smartImport.bankAndCardStatementsReadBy",
  },
  {
    icon: Landmark,
    name: "smartImport.ofxQfx",
    note: "smartImport.openFinancialExchangeDownloadsFromOnline",
  },
  {
    icon: ScanLine,
    name: "smartImport.pngJpg",
    note: "smartImport.photosAndScansOfPaperStatements",
  },
] as const;

function Screenshot({
  src,
  alt,
  width,
  height,
  eager = false,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  eager?: boolean;
}) {
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : undefined}
      decoding="async"
    />
  );
}

function ImportActions() {
  const { t } = useTranslation();
  return (
    <div className="import-actions">
      <a
        className="import-button import-button-primary"
        href={productUrl(SIGN_UP_AUTH, "smart_import")}
      >
        {t("common.startFreeTrial")}
        <ArrowRight aria-hidden="true" size={18} />
      </a>
      <a
        className="import-button import-button-secondary"
        href={productUrl(SMART_IMPORT_DEMO_URL, "smart_import")}
      >
        {t("smartImport.tryTheLiveDemo")}
      </a>
    </div>
  );
}

function Walkthrough() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const step = STEPS[active];
  return (
    <section
      className="import-section import-walkthrough"
      aria-labelledby="import-workflow-title"
    >
      <div className="import-section-heading">
        <h2 id="import-workflow-title">
          {t("smartImport.fourStepsFromFileToLedger")}
        </h2>
        <p>{t("smartImport.noImporterScriptsNoColumnMapping")}</p>
      </div>
      <ol className="import-step-list">
        {STEPS.map((item, index) => (
          <li key={item.title}>
            <button
              type="button"
              aria-pressed={active === index}
              aria-controls="import-step-preview"
              id={`import-step-${index}`}
              onClick={() => setActive(index)}
            >
              <span className="import-step-number" aria-hidden="true">
                {index + 1}
              </span>
              <span>{t(item.title)}</span>
            </button>
          </li>
        ))}
      </ol>
      <div
        id="import-step-preview"
        className="import-step-preview"
        role="region"
        aria-labelledby={`import-step-${active}`}
      >
        <div className="import-step-copy" aria-live="polite" aria-atomic="true">
          <h3>{t(step.title)}</h3>
          <p>{t(step.body)}</p>
        </div>
        <div className="import-step-image">
          <Screenshot
            src={step.img}
            alt={t(step.alt)}
            width={1600}
            height={step.height}
          />
        </div>
      </div>
    </section>
  );
}

export function SmartImportPage() {
  const { t } = useTranslation();
  return (
    <div className="smart-import-page">
      <AppHeader />
      <main id="main">
        <section className="import-hero">
          <div className="import-container">
            <div className="import-hero-copy">
              <h1>{t("smartImport.dropAFileGetCleanBeancount")}</h1>
              <div>
                <p>{t("smartImport.smartImportReadsACsvPdf")}</p>
                <ImportActions />
              </div>
            </div>
            <figure className="import-hero-preview">
              <figcaption>
                <span>
                  <Wand2 aria-hidden="true" size={18} />
                  {t("smartImport.categorizedToYourAccounts")}
                </span>
                <span>
                  <Check aria-hidden="true" size={18} />
                  {t("smartImport.youStayInControl")}
                </span>
              </figcaption>
              <Screenshot
                src="/img/smart-import/screenshot-categorize.png"
                alt={t("smartImport.smartImportConfigureStepAiFill")}
                width={1600}
                height={802}
                eager
              />
            </figure>
          </div>
        </section>
        <div className="import-container">
          <section
            className="import-section import-formats"
            aria-labelledby="import-formats-title"
          >
            <div className="import-section-heading">
              <h2 id="import-formats-title">
                {t("smartImport.worksWithTheFilesYouAlready")}
              </h2>
              <p>{t("smartImport.exportedFromYourBankDownloadedFrom")}</p>
            </div>
            <ul>
              {FORMATS.map(({ icon: Icon, name, note }) => (
                <li key={name}>
                  <Icon aria-hidden="true" size={25} />
                  <h3>{t(name)}</h3>
                  <p>{t(note)}</p>
                </li>
              ))}
            </ul>
          </section>
          <Walkthrough />
        </div>
        <section
          className="import-control"
          aria-labelledby="import-control-title"
        >
          <div className="import-container">
            <div className="import-section-heading">
              <h2 id="import-control-title">
                {t("smartImport.builtForAccurateAuditableBooks")}
              </h2>
              <p>{t("smartImport.speedWhereItHelpsControlWhere")}</p>
            </div>
            <div className="import-features">
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <article key={title}>
                  <Icon aria-hidden="true" size={24} />
                  <h3>{t(title)}</h3>
                  <p>{t(body)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
        <div className="import-container">
          <section
            className="import-section import-faq"
            aria-labelledby="import-faq-title"
          >
            <h2 id="import-faq-title">
              {t("common.frequentlyAskedQuestions2")}
            </h2>
            <div>
              {SMART_IMPORT_FAQ_KEYS.map(([questionKey, answerKey]) => (
                <details key={questionKey}>
                  <summary>
                    {t(questionKey)}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{t(answerKey)}</p>
                </details>
              ))}
            </div>
          </section>
          <section className="import-final">
            <h2>{t("smartImport.importYourFirstStatementInSeconds")}</h2>
            <p>{t("smartImport.dropAFileLetAiDo")}</p>
            <ImportActions />
          </section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
